import type { LiveWorksiteRow, LiveWorksiteWorker } from '@attendance/shared';
import type { LiveAttendanceRow } from '../../api/live.api.js';
import type { SiteManpowerRow, Worksite } from '../../api/worksites.api.js';
import {
  PEOPLE_EXCEPTIONS,
  countPeopleException,
  isOnClock,
  isUnmappedSite,
  type ExceptionClock,
} from './exceptions.js';

/**
 * The model behind the merged Operations control room — pure, so the four boards
 * it replaces (/live, /worksites/board, /worksites/live-board,
 * /worksites/allocations) stop being four different answers to the same
 * question and become four renderings of one join.
 *
 * The whole point of merging them is that a supervisor was previously asked to
 * reconcile the pages themselves: the map showed pins, the board showed a
 * manpower table, the live board showed per-site headcounts, and allocations
 * showed the planning roster — each over a different population, each silently
 * omitting the sites the others knew about. Here every site appears exactly
 * once, and every number carries the feed it came from, because these feeds
 * disagree by design (see `operationsKpis`).
 *
 * No React, no Leaflet, no i18n in this file: the counts on the KPI strip, the
 * rows in the table and the pins on the map must be provably the same objects,
 * and that is only worth anything if it can be asserted without mounting a map.
 */

// ─── Coordinates ──────────────────────────────────────────────────────────────

/**
 * Coordinates reach us in two shapes from two feeds of the SAME database
 * column: `Worksite.latitude` is a string (Prisma Decimal serialised as text),
 * `LiveWorksiteRow.latitude` is already a number. Normalising both here is the
 * only way a site can be pinned regardless of which feed happened to load.
 *
 * Two traps this closes, both of which have bitten map code before:
 *
 *   - `Number('')` is 0, and `Number(null)` is 0. An address-only ERP site with
 *     a blank coordinate would land itself in the Gulf of Guinea. Blank and
 *     absent must become null before any arithmetic sees them.
 *   - 0 is a LEGITIMATE coordinate. `lat || null` and `if (!lat)` both throw
 *     away the equator and the prime meridian, so nullish checks only, never
 *     truthiness.
 */
export function toCoord(value: string | number | null | undefined): number | null {
  if (value == null) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

// ─── The merged row ───────────────────────────────────────────────────────────

/**
 * One worksite, assembled from up to three feeds.
 *
 * Every count is `number | null`, and null means "this feed has not told us" —
 * never "zero". A site that no feed reported people for and a site whose feed
 * failed to load look identical if you coerce, and the coerced version is the
 * dangerous one: it reads as an empty site to the person deciding whether to
 * send a bus there.
 */
export interface OperationsSite {
  id: string;
  name: string;
  code: string | null;
  city: string | null;
  emirate: string | null;
  /**
   * From the roster feed only (`Worksite.type`) — null when this row was
   * raised by live/manpower alone and the roster never covered it. Used to
   * tell an intentionally-uncoordinated site (`REMOTE`) apart from a real
   * site an admin forgot to geocode — see `isUnmappedSite`.
   */
  type: string | null;
  /** Normalised from either feed; null when the site was never geocoded. */
  latitude: number | null;
  longitude: number | null;
  /** Both coordinates present and finite — i.e. this site can carry a pin. */
  mappable: boolean;
  /** Live per-site occupancy. Null when the live-headcount feed has no row here. */
  onSiteNow: number | null;
  capacity: number | null;
  overCapacity: boolean;
  longStay: boolean;
  /** Who is on site right now; empty when the live feed didn't cover this site. */
  workers: LiveWorksiteWorker[];
  /** Day-level attendance reckoning. Null when the manpower feed has no row. */
  assigned: number | null;
  present: number | null;
  /** SUBSET of `present`, not a sibling — see the note in `OperationsKpis`. */
  late: number | null;
  onLeave: number | null;
  absent: number | null;
  /** Planning roster headcount (`Worksite.assignedCount`); null when unknown. */
  allocated: number | null;
  /**
   * Which feeds contributed to this row. The UI needs this to render "—" rather
   * than a number: a column is blank because a feed is silent, and the reader
   * deserves to know which.
   */
  sources: { live: boolean; manpower: boolean; roster: boolean };
}

/**
 * The three feeds, each optional because each can independently be loading or
 * failed. Everything downstream must stay total under any combination of the
 * eight — a control room that throws when one endpoint 500s is worse than one
 * that shows partial truth clearly labelled.
 */
export interface OperationsFeeds {
  /** GET /site-ops/live-headcount — per-site occupancy right now. */
  live?: readonly LiveWorksiteRow[];
  /** GET /worksites/board/manpower — day-level attendance per site. */
  manpower?: readonly SiteManpowerRow[];
  /** GET /worksites, filtered to `isActive` — the planning roster of sites. */
  roster?: readonly Worksite[];
}

/**
 * Merge the feeds into one row per worksite.
 *
 * The join key is the worksite id, which all three feeds carry as a real id
 * (`worksiteId` on live, `id` on manpower and roster). Joining on NAME — which
 * an earlier board did — silently split "Marina Tower" from "Marina Tower "
 * and double-counted the site.
 *
 * The roster feed is included precisely because it is the only one that lists a
 * site with ZERO people on it: live-headcount emits nothing for an empty site
 * and manpower emits nothing for a site with no assignments today. Dropping
 * those rows would make an unstaffed site invisible on the very screen whose
 * job is to notice unstaffed sites, so a roster-only site appears with
 * `onSiteNow: null` and `sources.live === false`.
 *
 * Inactive roster sites do not create rows — a decommissioned site is noise —
 * but their metadata still enriches a row that live or manpower raised, because
 * if someone is punching at a site we deactivated, we want its name and
 * coordinates on screen, not a bare id.
 */
export function buildOperationsSites(input: OperationsFeeds): OperationsSite[] {
  const live = new Map<string, LiveWorksiteRow>();
  for (const row of input.live ?? []) live.set(row.worksiteId, row);

  const manpower = new Map<string, SiteManpowerRow>();
  for (const row of input.manpower ?? []) manpower.set(row.id, row);

  const roster = new Map<string, Worksite>();
  for (const row of input.roster ?? []) roster.set(row.id, row);

  // A site present in ANY feed appears exactly once. A Set keeps that "exactly
  // once" structural rather than something a later reader has to maintain.
  const ids = new Set<string>();
  for (const id of live.keys()) ids.add(id);
  for (const id of manpower.keys()) ids.add(id);
  for (const [id, site] of roster) if (site.isActive) ids.add(id);

  const sites: OperationsSite[] = [];
  for (const id of ids) {
    const l = live.get(id);
    const m = manpower.get(id);
    const r = roster.get(id);

    // `/worksites` is the record of truth for site metadata, so it wins where
    // it is present; the other feeds denormalise the same columns and are used
    // as fallbacks so a row is never nameless.
    const name = r?.name ?? l?.worksiteName ?? m?.name ?? id;
    const code = r?.code ?? l?.worksiteCode ?? m?.code ?? null;

    // Coordinates come from the same DB column via both feeds, so take whichever
    // actually parsed rather than trusting one feed's presence.
    const latitude = toCoord(r?.latitude) ?? toCoord(l?.latitude);
    const longitude = toCoord(r?.longitude) ?? toCoord(l?.longitude);

    sites.push({
      id,
      name,
      code,
      city: r?.city ?? l?.city ?? null,
      emirate: r?.emirate ?? l?.emirate ?? null,
      type: r?.type ?? null,
      latitude,
      longitude,
      mappable: latitude != null && longitude != null,
      onSiteNow: l ? l.headcount : null,
      capacity: l?.capacity ?? r?.capacity ?? null,
      // Flags are assertions the live feed makes; with no live row there is
      // nothing to assert, and `false` here means "not flagged", not "unknown".
      overCapacity: l?.flagOverCapacity ?? false,
      longStay: l?.flagLongStay ?? false,
      workers: l ? [...l.workers] : [],
      assigned: m ? m.assigned : null,
      present: m ? m.present : null,
      late: m ? m.late : null,
      onLeave: m ? m.onLeave : null,
      absent: m ? m.absent : null,
      allocated: r?.assignedCount ?? null,
      sources: { live: l != null, manpower: m != null, roster: r != null },
    });
  }

  return sortOperationsSites(sites);
}

/**
 * Busiest first, then alphabetical.
 *
 * The control room is read top-down under time pressure, and a site with forty
 * people on it is the one worth looking at; the empty sites are context, not
 * headline. Ranking treats an unknown headcount as 0 so it sinks — an unknown
 * count is not evidence of activity — but the row keeps its null so the cell
 * still renders "—" rather than a fabricated zero.
 */
export function sortOperationsSites(
  sites: readonly OperationsSite[]
): OperationsSite[] {
  return [...sites].sort((a, b) => {
    const ra = a.onSiteNow ?? 0;
    const rb = b.onSiteNow ?? 0;
    if (ra !== rb) return rb - ra;
    return a.name.localeCompare(b.name);
  });
}

// ─── KPI strip ────────────────────────────────────────────────────────────────

/**
 * The feeds the KPI strip reads. `people` is the per-WORKER live board
 * (`/attendance/live`), which is a different population from the per-SITE
 * live headcount and is deliberately kept as its own input.
 */
export interface OperationsKpiFeeds extends OperationsFeeds {
  /** GET /attendance/live — one row per worker in scope. */
  people?: readonly LiveAttendanceRow[];
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * READ THIS BEFORE ADDING A "TOTAL ON SITE" NUMBER.
 *
 * "How many people are on site" is computable three ways from these feeds, and
 * THEY WILL DISAGREE. That is not a bug in any of them — they answer three
 * different questions:
 *
 *   `onSiteNow`   /site-ops/live-headcount sums per-site occupancy over a 24h
 *                 lookback, and a BREAK_END leaves the leg OPEN — so a night
 *                 shift that started yesterday evening still counts, and so
 *                 does someone back from lunch.
 *   `onClock`     /attendance/live counts rows whose status is 'IN', TODAY
 *                 only — so the same night shift is invisible here.
 *   `presentToday` manpower's `present` is a day-level attendance reckoning:
 *                 weekend- and holiday-aware, derived from AttendanceDay, and
 *                 it will count someone who has already gone home.
 *
 * Never average them, never sum them, never pick "the one that looks right",
 * and never quietly fall back from one to another. They are returned as
 * separately-named, separately-sourced fields so the UI can label each with
 * the question it answers. Blending them produces a number no one can defend
 * to a client asking why the invoice says 40 and the screen says 37.
 *
 * `late` is a SUBSET of `present` in the manpower feed, not a sibling state.
 * present + late is double counting; present already includes the latecomers.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Every count is `number | null`, where null means "that feed has not answered
 * yet" — loading, or failed. It must NOT be coerced to 0 anywhere between here
 * and the screen. A supervisor reading a confident `0` when the fetch failed
 * concludes the site is empty and acts on it; that exact defect has already
 * been fixed twice elsewhere in this codebase and is not coming back through
 * this door.
 *
 * The site tallies are plain numbers, not nullable: they are counted from the
 * merged rows we actually hold, so "0 sites" is a true statement about what we
 * know rather than a claim about the world.
 */
export interface OperationsKpis {
  /** Sum of live per-site headcounts. 24h lookback; open BREAK_END counts. */
  onSiteNow: number | null;
  /** Live sites reporting at least one person. */
  sitesWithPeople: number | null;
  /** Per-worker live rows with status 'IN'. Today only. */
  onClock: number | null;
  /** Per-worker live rows with status 'NOT_PUNCHED'. */
  notPunched: number | null;
  /** Per-worker live rows whose latest event was FLAGGED. */
  flagged: number | null;
  /**
   * Per-worker live rows whose last event was a PUNCH_OUT more than two hours
   * ago while their working day is still running — the "left somewhere,
   * arrived nowhere" observation (`exceptions.ts`).
   *
   * `null` when the people feed has not answered OR when no clock was passed:
   * this is the one figure on the strip that cannot be derived from the feed
   * alone, and a caller with no clock has not asked the question rather than
   * received the answer nought.
   */
  unaccounted: number | null;
  /** Manpower sums — day-level, weekend/holiday aware. */
  assignedToday: number | null;
  presentToday: number | null;
  absentToday: number | null;
  onLeaveToday: number | null;
  /** Counted from the merged rows, so always known. */
  mappedSites: number;
  unmappedSites: number;
  activeSites: number;
}

/**
 * HOW MANY PEOPLE ARE ON SITE RIGHT NOW — the one implementation.
 *
 * The KPI strip's "On site now" tile and the live count on the rail's
 * Operations row are the same claim about the same minute, so they are the same
 * arithmetic over the same feed rather than two sums that happen to agree
 * today (rule 3.2). Pulled out of `operationsKpis` so the rail can reach it
 * without building the merged site rows it does not need.
 *
 * `undefined` in, `null` out: the feed has not answered, which is not zero. An
 * EMPTY array is a real answer and returns 0 — nobody is on site.
 */
export function sumOnSiteNow(
  live: readonly LiveWorksiteRow[] | undefined
): number | null {
  if (!live) return null;
  let total = 0;
  for (const row of live) total += row.headcount;
  return total;
}

/**
 * The KPI strip.
 *
 * Takes the merged sites AND the raw feeds on purpose. The site tallies must
 * agree with the table the reader is looking at, so they are counted from the
 * same array; the population counts are taken from the raw feeds so that an
 * empty-but-loaded feed reports a truthful 0 while an absent feed reports null.
 * Deriving nullness from the merged rows instead would collapse those two
 * cases — with no live rows, every merged site has `onSiteNow: null`, which
 * says nothing about whether the endpoint answered.
 */
export function operationsKpis(
  sites: readonly OperationsSite[],
  feeds: OperationsKpiFeeds,
  /**
   * The page's one instant, in the company timezone. Only "Unaccounted" needs
   * it, and it is OPTIONAL rather than defaulted on purpose: a default would
   * be this file inventing a clock — and, worse, a different one from the
   * screen's, so the tile could count a row the roster does not. Omitted, the
   * figure is `null` and the tile renders an em-dash with no control on it.
   */
  clock?: ExceptionClock
): OperationsKpis {
  const { live, manpower, people } = feeds;

  const onSiteNow = sumOnSiteNow(live);
  let sitesWithPeople: number | null = null;
  if (live) {
    sitesWithPeople = 0;
    for (const row of live) {
      if (row.headcount > 0) sitesWithPeople += 1;
    }
  }

  let onClock: number | null = null;
  let notPunched: number | null = null;
  let flagged: number | null = null;
  if (people) {
    onClock = 0;
    notPunched = 0;
    flagged = 0;
    for (const row of people) {
      if (isOnClock(row)) onClock += 1;
      // THE TWO EXCEPTION COUNTS GO THROUGH `exceptions.ts`, and that is the
      // whole reason the tiles above them can be links. The People panel
      // FILTERS with these same two functions, so "Not punched 12" opening a
      // list of twelve is arithmetic rather than a coincidence two files have
      // to keep agreeing on. Inlining either condition here re-opens the gap.
      if (PEOPLE_EXCEPTIONS.notPunched(row)) notPunched += 1;
      // Flagged is a SECOND axis, not a status: a flagged row is also counted
      // in whichever status bucket it belongs to.
      if (PEOPLE_EXCEPTIONS.flagged(row)) flagged += 1;
    }
  }

  // The third exception, counted through the SAME door the People panel
  // filters through. It is not folded into the loop above because it is the
  // one figure that can be `null` while the feed itself answered — no clock,
  // no question — and `countPeopleException` is where that rule lives.
  const unaccounted = countPeopleException(people, 'unaccounted', clock);

  let assignedToday: number | null = null;
  let presentToday: number | null = null;
  let absentToday: number | null = null;
  let onLeaveToday: number | null = null;
  if (manpower) {
    assignedToday = 0;
    presentToday = 0;
    absentToday = 0;
    onLeaveToday = 0;
    for (const row of manpower) {
      assignedToday += row.assigned;
      presentToday += row.present;
      absentToday += row.absent;
      onLeaveToday += row.onLeave;
      // `row.late` is deliberately NOT summed into anything here: it is already
      // inside `present`, and a "late today" KPI would need its own field.
    }
  }

  // Same contract as the two people counts: `unmappedSites` is the length of
  // the set the "No coordinates" tile opens, because the Sites table filters
  // with `isUnmappedSite` too.
  let unmapped = 0;
  for (const site of sites) if (isUnmappedSite(site)) unmapped += 1;
  const mappedSites = sites.length - unmapped;

  return {
    onSiteNow,
    sitesWithPeople,
    onClock,
    notPunched,
    flagged,
    unaccounted,
    assignedToday,
    presentToday,
    absentToday,
    onLeaveToday,
    mappedSites,
    unmappedSites: unmapped,
    activeSites: sites.length,
  };
}

// ─── Search ───────────────────────────────────────────────────────────────────

/**
 * Free text over the four things a supervisor actually types: the site name,
 * its code, its city and its emirate.
 *
 * One lowercased haystack per row rather than four `includes` calls, so a query
 * spanning fields behaves predictably and the cost stays linear on the 30s
 * refetch. Search deliberately does NOT look at worker names — the site table
 * is a site index, and matching a site because someone standing on it shares a
 * name with another site turns the filter into a lucky dip.
 */
export function matchesSite(site: OperationsSite, search: string): boolean {
  const q = search.trim().toLowerCase();
  if (!q) return true;
  const haystack = [site.name, site.code ?? '', site.city ?? '', site.emirate ?? '']
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
}

/** Empty / whitespace-only search returns everything, in the order given. */
export function filterSites(
  sites: readonly OperationsSite[],
  search: string
): OperationsSite[] {
  const q = search.trim();
  if (!q) return [...sites];
  return sites.filter((site) => matchesSite(site, q));
}
